
import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch,
  Ref,
  onBeforeMount,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  ParserFieldObject, Property, StepperFieldConfig, StepperSignEnum,
} from '@/types';
import BaseInputNumber from '@/components/fields/partials/BaseInputNumber.vue';
import ToggleDisplaySettings from '@/components/template-builder/fields/partials/ToggleDisplaySettings.vue';

export default defineComponent({
  name: 'Stepper',

  components: {
    ToggleDisplaySettings,
    BaseInputNumber,
  },

  props: {
    configs: {
      type: Object as PropType<StepperFieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },

    elementPartType: {
      type: String,
      required: false,
      default: '',
    },

    disableHandler: {
      type: Function,
      required: false,
      default: null,
    },

    disableMessage: {
      type: String,
      required: false,
      default: '',
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const isDisabled = computed(() => (props.disableHandler ? props.disableHandler() === true : false));
    const configs = ref(props.configs);
    const displayProperties = ref(!(!Object.prototype.hasOwnProperty.call(configs.value, 'displayToggle') || configs.value.displayToggle === true));
    const defaultValue = 0;
    const currentValue = ref(defaultValue);
    const properties: Ref<Property[]> = ref(props.parserValues.properties);
    const isInitialized = ref(false);
    const handleInput = ({ value }: { value: number | null }) => {
      if (value === null) {
        currentValue.value = defaultValue;
      }
    };

    onBeforeMount(() => {
      if (properties.value) {
        let parserValue = properties.value[0].value;
        if (parserValue) {
          const replacement = new RegExp(`[${Object.values(StepperSignEnum).toString()}]`, 'g');
          parserValue = parserValue.replace(replacement, '');
          if (!Number.isNaN(Number(parserValue))) {
            currentValue.value = Number(parserValue);
          }
        }
      }
      // Mark as initialized after reading initial value so first user change triggers setter
      isInitialized.value = true;
    });

    watch(currentValue, (values, oldValues) => {
      if (Number(values) !== Number(oldValues)) {
        const propertiesToEmit = properties.value.reduce(
          (acc: Property[], property: Property) => {
            const propertyClone = { ...property };
            // eslint-disable-next-line no-param-reassign
            propertyClone.value = `${currentValue.value ?? defaultValue}${props.configs.options.sign}`;
            acc.push(propertyClone);
            return acc;
          },
          [],
        );

        if (isInitialized.value) {
          context.emit('on-change-properties', {
            selector: props.parserValues.selector,
            properties: propertiesToEmit,
          });
        }

        isInitialized.value = true;
      }
    });

    return {
      displayProperties,
      currentValue,
      isDisabled,
      handleInput,
      t,
    };
  },
});
